/**
 * useCourseCustomCode
 *
 * Client-only composable that watches the active course product's custom code
 * (customJs, customCss, customHeader, customFooter) and injects/removes
 * styles, scripts, and tracking code into the live DOM.
 *
 * Mirrors the established patterns in useGroupCustomCode and useCPCustomCode,
 * ported to course products in Client Portal and GoKollab.
 *
 * Injection is scoped to active course product routes:
 *   - /courses/products/:productId (overview, categories, etc.)
 *   - /courses/products/:productId/modules/:moduleId/lessons/:lessonId
 *   - /courses/products/:productId/live-sessions
 *
 * Injected elements are tagged with `data-course-custom-code` so they can
 * be cleanly removed when navigating away from the course or unmounting.
 * Head <style> / <script> also use stable IDs:
 *   - COURSE_CUSTOM_CODE_INJECTED_STYLE_ID
 *   - COURSE_CUSTOM_CODE_INJECTED_SCRIPT_ID
 */

import { computed, onMounted, onUnmounted, watch } from 'vue'

import { useRoute } from 'nuxt/app'

import type { IProductDetail } from '@courses/types-layer/types/product'
import { useLocationStore } from '../../../common/location-layer/store/location.store'
import { useSentry } from '../../../common/error-monitoring-layer/composables/useSentry'

import { useProductQueryStore } from '../stores/product.query-store'
import type { CourseCustomCode } from '../types/course-custom-code.types'

export const COURSE_CUSTOM_CODE_ATTR = 'data-course-custom-code'

/** Stable head element IDs for injected CSS/JS (tests and deterministic DOM queries). */
export const COURSE_CUSTOM_CODE_INJECTED_STYLE_ID = 'course-custom-code-injected-css'
export const COURSE_CUSTOM_CODE_INJECTED_SCRIPT_ID = 'course-custom-code-injected-js'

export const COURSE_CUSTOM_CODE_HEADER_ANCHOR_ID = 'courses-siteCustomHeader'
export const COURSE_CUSTOM_CODE_HEADER_LEGACY_ANCHOR_ID = 'productCustomHeader'
export const COURSE_CUSTOM_CODE_FOOTER_ANCHOR_ID = 'courses-siteCustomFooter'
export const COURSE_CUSTOM_CODE_FOOTER_LEGACY_ANCHOR_ID = 'productCustomFooter'

/**
 * Checks if the route represents an active course product surface.
 */
export function isActiveCourseProductRoute(route: { path?: string; params?: Record<string, unknown> }): boolean {
  const pid = route.params?.productId
  if (typeof pid === 'string' && pid.trim().length > 0) {
    return true
  }
  return typeof route.path === 'string' && /^\/courses\/products\/([^/]+)/.test(route.path)
}

/**
 * Extracts productId from route params or path.
 */
export function extractCourseProductId(route: { path?: string; params?: Record<string, unknown> }): string | null {
  const pid = route.params?.productId
  if (typeof pid === 'string' && pid.trim().length > 0) {
    return pid.trim()
  }
  const match = route.path?.match(/^\/courses\/products\/([^/]+)/)
  return match?.[1] ?? null
}

/** Serialise course custom code to a stable string for content-equality checks. */
export function serializeCourseCustomCode(productId: string, code: CourseCustomCode): string {
  return JSON.stringify({
    productId,
    customJs: code.customJs ?? '',
    customCss: code.customCss ?? '',
    headerCode: code.headerCode ?? '',
    footerCode: code.footerCode ?? '',
  })
}

/**
 * Wrap raw JS in a try/catch so errors in customer code don't propagate to
 * our own error monitoring. Mirrors sanitizeJavaScriptCode.
 */
export function sanitizeCourseJs(jsCode: string): string {
  const TAG_PATTERN = /<\/?[a-zA-Z][^<>]*>/g
  let stripped = jsCode
  let previous: string
  do {
    previous = stripped
    stripped = previous.replace(TAG_PATTERN, '')
  } while (stripped !== previous)

  return `try {
  ${stripped.trim()}
} catch (error) {
  console.error('Course custom JS error:', error);
}`
}

/** Remove all previously injected course custom-code elements. */
export function removeInjectedCourseCustomCodeElements(): void {
  if (typeof document === 'undefined') return
  document.querySelectorAll(`[${COURSE_CUSTOM_CODE_ATTR}]`).forEach(el => el.remove())
}

function injectCourseCss(css: string): void {
  if (!css || typeof document === 'undefined') return
  const style = document.createElement('style')
  style.id = COURSE_CUSTOM_CODE_INJECTED_STYLE_ID
  style.setAttribute('type', 'text/css')
  style.setAttribute(COURSE_CUSTOM_CODE_ATTR, 'css')
  style.innerHTML = css
  document.head.appendChild(style)
}

function injectCourseJs(js: string): void {
  if (!js || typeof document === 'undefined') return
  const sanitized = sanitizeCourseJs(js)

  try {
    new Function(sanitized)
  } catch (e) {
    console.error('Course custom JS is invalid — skipping injection:', e)
    return
  }

  const script = document.createElement('script')
  script.id = COURSE_CUSTOM_CODE_INJECTED_SCRIPT_ID
  script.setAttribute('type', 'text/javascript')
  script.setAttribute(COURSE_CUSTOM_CODE_ATTR, 'js')
  script.innerHTML = sanitized
  document.head.appendChild(script)
}

function injectCourseHtml(primaryTargetId: string, secondaryTargetId: string, html: string, attrValue: string): void {
  if (!html || typeof document === 'undefined') return
  const target = document.getElementById(primaryTargetId) ?? document.getElementById(secondaryTargetId) ?? document.body
  if (!target) return

  const normalized = html.includes('<') ? html : `<script>${html}<\/script>`

  const parser = new DOMParser()
  const doc = parser.parseFromString(normalized, 'text/html')

  const allNodes = [...Array.from(doc.head.childNodes), ...Array.from(doc.body.childNodes)]

  allNodes.forEach(node => {
    if (node.nodeName === 'SCRIPT') {
      const original = node as HTMLScriptElement
      const script = document.createElement('script')
      script.setAttribute(COURSE_CUSTOM_CODE_ATTR, attrValue)
      if (original.src) {
        script.src = original.src
        script.async = original.async
      } else {
        script.innerHTML = original.innerHTML
      }
      Array.from(original.attributes).forEach(attr => {
        if (attr.name !== 'src') script.setAttribute(attr.name, attr.value)
      })
      target.appendChild(script)
    } else {
      const clone = (node as Element).cloneNode?.(true) as Element
      if (clone && clone.setAttribute) clone.setAttribute(COURSE_CUSTOM_CODE_ATTR, attrValue)
      if (clone) target.appendChild(clone)
    }
  })
}

export function applyCourseCustomCode(code: CourseCustomCode): void {
  removeInjectedCourseCustomCodeElements()

  injectCourseCss(code.customCss ?? '')
  injectCourseJs(code.customJs ?? '')
  injectCourseHtml(COURSE_CUSTOM_CODE_HEADER_ANCHOR_ID, COURSE_CUSTOM_CODE_HEADER_LEGACY_ANCHOR_ID, code.customHeader ?? '', 'header')
  injectCourseHtml(COURSE_CUSTOM_CODE_FOOTER_ANCHOR_ID, COURSE_CUSTOM_CODE_FOOTER_LEGACY_ANCHOR_ID, code.customFooter ?? '', 'footer')
}

/**
 * Call this composable once in CPRootContainer / root layout.
 * Watches the active course product and applies custom code when on a course route.
 */
export function useCourseCustomCode(): void {
  if (process.server) return

  const route = useRoute()
  const locationStore = useLocationStore()
  const queryStore = useProductQueryStore(() => locationStore.locationId)
  const { setTag } = useSentry()

  let lastAppliedContent: string | null = null

  const activeProductId = computed(() => {
    if (!isActiveCourseProductRoute(route)) return ''
    return extractCourseProductId(route) ?? ''
  })

  const productQuery = computed(() => {
    const pid = activeProductId.value
    if (!pid) return null
    return queryStore.fetchProductQuery(pid)
  })

  const productDetail = computed<IProductDetail | null>(() => {
    return productQuery.value?.data?.value ?? null
  })

  function applyIfChanged(pid: string, product: IProductDetail | null): void {
    if (!pid || !product) {
      if (lastAppliedContent !== null) {
        lastAppliedContent = null
        removeInjectedCourseCustomCodeElements()
        setTag('customCode', 'false')
      }
      return
    }

    const code: CourseCustomCode = {
      customJs: product.customJs,
      customCss: product.customCss,
      customHeader: product.customHeader,
      customFooter: product.customFooter,
    }

    const content = serializeCourseCustomCode(pid, code)
    if (content === lastAppliedContent) return
    lastAppliedContent = content

    const hasCustomCode = Boolean(code.customJs || code.customCss || code.customHeader || code.customFooter)
    setTag('customCode', hasCustomCode ? 'true' : 'false')

    if (hasCustomCode) {
      applyCourseCustomCode(code)
    } else {
      removeInjectedCourseCustomCodeElements()
    }
  }

  onMounted(() => {
    applyIfChanged(activeProductId.value, productDetail.value)
  })

  watch(
    [() => activeProductId.value, () => productDetail.value],
    ([pid, prod]) => {
      applyIfChanged(pid, prod)
    },
    { immediate: true }
  )

  onUnmounted(() => {
    lastAppliedContent = null
    removeInjectedCourseCustomCodeElements()
  })
}
